import { useState } from 'preact/hooks'
import { Loader2 } from 'lucide-preact'
import { sendingDomainsApi } from '@/api/sendingDomains'
import type { SendingDomainViewDto } from '@/types/sendingDomains'
import { useToast } from '@/hooks/useToast'
import { ToggleRow } from '@/components/common/ToggleRow'

interface SendingDomainTrackingSectionProps {
  clientCompanyId: string
  domain: SendingDomainViewDto
  onUpdated: (domain: SendingDomainViewDto) => void
}

/**
 * Per-domain open/click tracking config. A custom tracking subdomain routes Resend's tracking links
 * through a subdomain of this domain, so clicks register reliably and deliverability stays higher
 * than the shared tracker (which strict recipient mail security often blocks). The generated CNAME
 * joins the DNS records above to verify.
 */
export function SendingDomainTrackingSection({ clientCompanyId, domain, onUpdated }: SendingDomainTrackingSectionProps) {
  const { showError, showSuccess } = useToast()
  const configured = !!domain.TrackingSubdomain
  const trackingHost = configured ? `${domain.TrackingSubdomain}.${domain.Domain}` : null

  const [subdomain, setSubdomain] = useState(domain.TrackingSubdomain ?? 'links')
  // Opinionated defaults: click on (the reliable signal), open off (Resend flags it inaccurate).
  // Reflect the live config once a subdomain is set.
  const [clickTracking, setClickTracking] = useState(configured ? domain.ClickTrackingEnabled : true)
  const [openTracking, setOpenTracking] = useState(domain.OpenTrackingEnabled)
  const [saving, setSaving] = useState(false)

  const handleConfigure = async () => {
    const trimmed = subdomain.trim()
    // Only send the subdomain when it actually changes. Re-sending the current value would spend
    // Resend's once-per-24h change budget on a no-op; a blank/omitted value leaves it untouched.
    const subdomainChanged = trimmed !== (domain.TrackingSubdomain ?? '')
    setSaving(true)
    const res = await sendingDomainsApi.configureTracking(clientCompanyId, domain.Id, {
      TrackingSubdomain: subdomainChanged ? (trimmed || undefined) : undefined,
      ClickTracking: clickTracking,
      OpenTracking: openTracking,
    })
    setSaving(false)
    if (res.error || !res.data) {
      showError('Could not configure tracking', res.error || 'Unknown error')
      return
    }
    // Reconcile local state with the persisted config (the API lowercases the subdomain).
    setSubdomain(res.data.TrackingSubdomain ?? trimmed)
    setClickTracking(res.data.ClickTrackingEnabled)
    setOpenTracking(res.data.OpenTrackingEnabled)
    onUpdated(res.data)
    showSuccess('Tracking configured', 'Add the new tracking CNAME in the DNS records above, then verify.')
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Open and click tracking</h3>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          Route tracking links through a subdomain of this domain so clicks register reliably and deliverability stays
          higher than the shared tracker, which strict recipient mail security often blocks.
        </p>
      </div>

      {configured ? (
        <p className="text-xs text-green-700 dark:text-green-400">
          Live on <code className="font-mono">{trackingHost}</code>.
        </p>
      ) : (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          On Resend's shared tracking domain. Clicks may not register for recipients with strict mail security.
        </p>
      )}

      <div className="max-w-md space-y-3 pt-1">
        <div className="space-y-1">
          <label htmlFor="tracking-subdomain" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Tracking subdomain
          </label>
          <div className="flex items-stretch rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
            <input
              id="tracking-subdomain"
              type="text"
              value={subdomain}
              onInput={e => setSubdomain((e.target as HTMLInputElement).value)}
              disabled={saving}
              placeholder="links"
              className="flex-1 min-w-0 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none disabled:opacity-60"
            />
            <span className="flex items-center px-3 font-mono text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 border-l border-slate-200 dark:border-slate-600">
              .{domain.Domain}
            </span>
          </div>
        </div>

        <div>
          <ToggleRow label="Click tracking" checked={clickTracking} onChange={setClickTracking} disabled={saving} />
          <ToggleRow
            label="Open tracking"
            checked={openTracking}
            onChange={setOpenTracking}
            disabled={saving}
            labelAdornment={
              <span className="rounded bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                Not recommended
              </span>
            }
          />
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            Open tracking relies on a pixel many clients block, so counts run inaccurate and it can reduce deliverability.
          </p>
        </div>

        <button
          type="button"
          onClick={handleConfigure}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          {configured ? 'Update tracking' : 'Enable tracking'}
        </button>
        <p className="text-xs text-slate-400 dark:text-slate-500">
          Changes apply at most once every 24 hours, and a tracking subdomain can't be removed once set.
        </p>
      </div>
    </div>
  )
}
